import { useState, useEffect } from 'react';
import { 
    collection, 
    query, 
    where, 
    onSnapshot, 
    doc, 
    updateDoc,
    setDoc,
    getDocs,
    getDoc
} from 'firebase/firestore';
import { db, auth } from '../../Services/firebase-config';

export const useHackathonTeam = (eventId) => {
    const [currentTeam, setCurrentTeam] = useState(null);
    const [teamMembers, setTeamMembers] = useState([]);
    const [isTeamLead, setIsTeamLead] = useState(false);
    const [teamLeaderData, setTeamLeaderData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!auth.currentUser || !eventId) {
            setIsLoading(false);
            return;
        }

        const userTeamQuery = query(
            collection(db, "hackathonTeams"),
            where("eventId", "==", eventId),
            where("memberIds", "array-contains", auth.currentUser.uid)
        );

        // Set up real-time listener for team data
        const unsubscribe = onSnapshot(
            userTeamQuery,
            (snapshot) => {
                try {
                    if (snapshot.empty) {
                        // User is not in any team
                        setCurrentTeam(null);
                        setTeamMembers([]);
                        setIsTeamLead(false);
                        setTeamLeaderData(null);
                        setError(null);
                    } else {
                        // User is in a team
                        const teamDoc = snapshot.docs[0];
                        const teamData = { id: teamDoc.id, ...teamDoc.data() };
                        setCurrentTeam(teamData);

                        // Check if current user is team leader
                        const isLead = teamData.createdBy === auth.currentUser.uid;
                        setIsTeamLead(isLead);

                        // Process team members
                        const memberDetails = teamData.members.map((member) => ({
                            id: member.id || member.userId,
                            displayName: member.name || member.displayName,
                            email: member.email,
                            phoneNumber: member.phoneNumber,
                            photoURL: member.photoURL || "",
                            skills: member.skills || [],
                            isOwner: (member.id || member.userId) === teamData.createdBy,
                        }));

                        setTeamMembers(memberDetails);

                        // Get team leader data
                        const leaderData = memberDetails.find((member) => member.isOwner);
                        setTeamLeaderData(leaderData);

                        setError(null);
                    }
                } catch (err) {
                    console.error('Error processing team data:', err);
                    setError(err.message);
                } finally {
                    setIsLoading(false);
                }
            },
            (err) => {
                console.error('Error listening to team changes:', err);
                setError(err.message);
                setIsLoading(false);
            }
        );

        return () => unsubscribe();
    }, [eventId]);

    // Function to update team data
    const updateTeam = async (teamId, updateData) => {
        try {
            const teamRef = doc(db, "hackathonTeams", teamId);
            await updateDoc(teamRef, {
                ...updateData,
                updatedAt: new Date(),
            });
            return { success: true };
        } catch (error) {
            console.error('Error updating team:', error);
            return { success: false, error: error.message };
        }
    };

    // Function to mark team as applied
    const markTeamAsApplied = async (teamId, applicationId) => {
        try {
            const teamRef = doc(db, "hackathonTeams", teamId);
            await updateDoc(teamRef, {
                hasApplied: true,
                applicationSubmittedAt: new Date(),
                applicationId: applicationId,
                updatedAt: new Date(),
            });
            return { success: true };
        } catch (error) {
            console.error('Error marking team as applied:', error);
            return { success: false, error: error.message };
        }
    };

    return {
        currentTeam,
        teamMembers,
        isTeamLead,
        teamLeaderData,
        isLoading,
        error,
        updateTeam,
        markTeamAsApplied,
    };
};